import React, { useContext, useRef, useState } from "react";
import { ALERT_TYPE, REQUEST_METHOD, REQUEST_METHOD as RM, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis";
import { ServerRequest } from "../../utils/apiweb";
import { getDateToString } from "../../utils/convert";
import {
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch,
} from "../../components/common";
import CertificadoEscribanoModal from "../../components/controls/CertificadoEscribanoModal";
import DataTaggerModalApi from "../../components/controls/DataTaggerModalApi";
import { useEntidad } from "../../components/hooks/useEntidad";
import { ManagedContext } from "../../context/custom";
import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext";
import { standardRequest } from "../../utils/requests";
import { PERMISSIONS } from "../../consts/permissions";
import { usePermissions } from "../../components/hooks/usePermissions";
import {TIPS} from "../../consts/tips";

function CertificadosEscribanoView() {
    const { setIsLoading, showMessageModal } = useContext(ManagedContext);

    const [state, setState] = useState({
        showForm: false,
        modeFormEdit: false,
        rowId: 0,
        dataTagger: {
            showModal: false,
            idEntidad: null,
        },
        list: [],
    });

    const [tableVisible, setTableVisible] = useState(false);

    const searchRef = useRef();

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.CERTIFICADO_ESCRIBANO_EDIT);

    const [, getRowEntidad] = useEntidad({
        entidades: ["ObjetoCertificado"],
        onLoaded: (entidades, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: "ObjetoCertificado",
            timeout: 0,
        },
    });

    const getObjeto = (id) => {
        const row = getRowEntidad("ObjetoCertificado", id);
        return row ? row.nombre : "";
    };

    const tableColumns = [
        { Header: "Año", accessor: "anioCertificado", width: "10%" },
        { Header: "N° de certificado", accessor: "numeroCertificado", width: "15%" },
        {
            Header: "Objeto",
            Cell: (data) => getObjeto(data.value),
            accessor: "idObjetoCertificado",
            width: "20%",
        },
        { Header: "Transferencia", accessor: "transferencia", width: "30%" },
        {
            Header: "Fecha entrada",
            Cell: (data) => (data.value ? getDateToString(data.value, false) : ""),
            accessor: "fechaEntrada",
            width: "15%",
        },
    ];

    const avmr = {
        onAdd: () => handleClickAdd(),
        onView: (id) => {
            setState((prevState) => ({
                ...prevState,
                showForm: true,
                modeFormEdit: false,
                rowId: parseInt(id),
            }));
        },
        onModify: ({ id }) => {
            setState((prevState) => ({
                ...prevState,
                showForm: true,
                modeFormEdit: true,
                rowId: parseInt(id),
            }));
        },
        onRemove: ({ id }) => {
            const certificado = state.list.find((x) => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar el certificado ${certificado.numeroCertificado}?`,
                onConfirm: () => {
                    setIsLoading(true);
                    standardRequest(RM.DELETE, `${APIS.URLS.CERTIFICADO_ESCRIBANO}/${id}`)
                        .then(() => SearchCertificadosEscribano())
                        .catch(() => {})
                        .finally(() => setIsLoading(false));
                },
            });
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setState((prevState) => ({
                ...prevState,
                dataTagger: { showModal: true, idEntidad: id, disabled },
            }));
        },
    };

    const handleClickAdd = () => {
        setState((prevState) => ({
            ...prevState,
            showForm: true,
            modeFormEdit: true,
            rowId: 0,
        }));
    };

    function SearchCertificadosEscribano() {
        setState((prev) => ({ ...prev, list: [] }));
        setIsLoading(true);

        const {
            anioCertificado,
            numeroCertificado,
            idTipoCertificado,
            idEscribano,
            idCuenta,
            etiqueta,
        } = searchRef.current?.filters || {};

        const paramsUrl =
            `/filter?` +
            `anioCertificado=${anioCertificado ?? ""}&` +
            `numeroCertificado=${encodeURIComponent(numeroCertificado ?? "")}&` +
            `idTipoCertificado=${encodeURIComponent(idTipoCertificado ?? 0)}&` +
            `idEscribano=${encodeURIComponent(idEscribano ?? 0)}&` +
            `idCuenta=${encodeURIComponent(idCuenta ?? 0)}&` +
            `etiqueta=${encodeURIComponent(etiqueta ?? "")}`;

        const callbackSuccess = (response) => {
            response
                .json()
                .then((data) => {
                    data.forEach((x) => {
                        if (x.fechaCreacion) x.fechaCreacion = new Date(x.fechaCreacion);
                    });
                    setState((prev) => ({ ...prev, list: data }));
                    setTableVisible(true);
                    setIsLoading(false);
                })
                .catch((error) => {
                    const message = "Error procesando respuesta: " + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setIsLoading(false);
                });
        };

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CERTIFICADO_ESCRIBANO,
            paramsUrl,
            null,
            callbackSuccess,
            (res) => {
                res.json().then((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                    setIsLoading(false);
                });
            },
            (error) => {
                ShowToastMessage(
                    ALERT_TYPE.ALERT_ERROR,
                    "Error procesando solicitud: " + error.message
                );
                setIsLoading(false);
            }
        );
    }

    const onSearch = () => {
        SearchCertificadosEscribano();
    };

    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? `el certificado ${dataTaggerRow.numeroCertificado}/${dataTaggerRow.anioCertificado}` : ''

    return (
        <>
            {state.dataTagger.showModal && (
                <DataTaggerModalApi
                    title="Información adicional de certificado escribano"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_CERTIFICADO_ESCRIBANO_MODAL}
                    entidad="CertificadoEscribano"
                    idEntidad={state.dataTagger.idEntidad}
                    disabled={false}
                    onConfirm={() => {
                        setState((prevState) => ({
                            ...prevState,
                            dataTagger: { showModal: false, idEntidad: null },
                        }));
                    }}
                    onDismiss={() => {
                        setState((prevState) => ({
                            ...prevState,
                            dataTagger: { showModal: false, idEntidad: null },
                        }));
                    }}
                />
            )}

            {state.showForm && (
                <CertificadoEscribanoModal
                    id={state.rowId}
                    disabled={!state.modeFormEdit}
                    onDismiss={() => {
                        setState((prevState) => ({
                            ...prevState,
                            showForm: false,
                        }));
                    }}
                    onConfirm={() => {
                        setState((prevState) => ({
                            ...prevState,
                            showForm: false,
                            rowId: 0,
                        }));
                        if (tableVisible) SearchCertificadosEscribano();
                    }}
                />
            )}

            <SectionHeading
                titles={[{ title: "Escribanos" }, { title: "Certificados" }]}
                onAddClick={canEdit ? handleClickAdd : null}
                tableVisible={tableVisible}
            />

            <section className="section-accordion">
                <WrapperAdvancedSearch
                    ref={searchRef}
                    filters={[
                        {
                            title: "Año certificado",
                            field: "anioCertificado",
                            type: "ejercicio",
                            size: 2,
                            searchOnEnter: true,
                        },
                        {
                            title: "Número certificado",
                            field: "numeroCertificado",
                            type: "string",
                            size: 2,
                            searchOnEnter: true,
                        },
                        {
                            title: "Tipo certificado",
                            field: "idTipoCertificado",
                            type: "list",
                            lista: "TipoCertificado",
                            size: 3,
                        },
                        {
                            title: "Escribano",
                            field: "idEscribano",
                            type: "entity",
                            tipName: TIPS.NAME.MODALS.ESCRIBANO_MODAL,
                            entidad: "Escribano",
                            size: 5,
                        },
                        {
                            title: "Cuenta",
                            field: "idCuenta",
                            type: "cuenta",
                            size: 2,
                        },
                        {
                            title: "Etiqueta",
                            field: "etiqueta",
                            type: "string",
                            size: 2,
                            searchOnEnter: true,
                        },
                    ]}
                    onSearch={onSearch}
                />

                {tableVisible && (
                    <TableCustom
                        className={"TableCustomBase m-top-20"}
                        columns={tableColumns}
                        data={state.list}
                        avmr={avmr}
                        editCode={PERMISSIONS.CERTIFICADO_ESCRIBANO_EDIT}
                        messageEmpty="No se encontraron resultados"
                    />
                )}
            </section>
        </>
    );
}

export default CertificadosEscribanoView;

